import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Technology, UsersTechnology } from '../../store/types'
import { Autocomplete, Box, MenuItem, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { formatTechnologyType } from '../../store/utils'
import { StyledContainerColumnLeft, StyledContainerRowBetween } from '../../styles/ContainerStyles'
import { useGetTechnologiesQuery } from '../../store/redux/reducers/technologiesSlice'
import TechnologyLogo from '../Logo/TechnologyLogo'

type SelectTechnologyProps = {
  usersTechnologies: UsersTechnology[]
  replace: (technologies: UsersTechnology[]) => void
}

const sortByType = (array: Technology[]) => {
  return [...array].sort((a, b) => -b.type.localeCompare(a.type))
}

const checkTechnologyExistence = (array: UsersTechnology[], id: number) =>
  array.map(technology => technology.technologyId).includes(id)

const SelectTechnology = ({ usersTechnologies, replace }: SelectTechnologyProps) => {
  const { data: response, isLoading } = useGetTechnologiesQuery({ page: 0, size: 500 })

  let technologies: Technology[] = useMemo(() => {
    if (response) return response.content
    return []
  }, [response])

  const [options, setOptions] = useState<Technology[]>([])
  const hasBeenOpened = useRef(false)
  const [selected] = useState<Technology[]>([])

  const { t } = useTranslation()

  useEffect(() => {
    if (!hasBeenOpened.current) {
      setOptions(sortByType(technologies.filter(entry => !checkTechnologyExistence(usersTechnologies, entry.id))))
    }
  }, [technologies, usersTechnologies])

  return (
    <StyledContainerRowBetween sx={{ gap: 2 }}>
      <Autocomplete
        value={selected}
        options={options}
        multiple
        disableCloseOnSelect
        onChange={(_, value) => {
          hasBeenOpened.current = true
          let newUsersTechnologies = value.map(entry => {
            let technology: UsersTechnology = {
              technologyId: entry.id,
              name: entry.name,
              logo: entry.logo,
              type: entry.type,
              extension: entry.extension,
              level: 1,
            }
            return technology
          })
          replace([...newUsersTechnologies, ...usersTechnologies])
        }}
        onClose={() => {
          setOptions(sortByType(technologies.filter(entry => !checkTechnologyExistence(usersTechnologies, entry.id))))
        }}
        fullWidth
        disablePortal
        id="combo-box-all-technologies"
        groupBy={option => option.type}
        getOptionLabel={technology => technology.name}
        noOptionsText={
          isLoading
            ? t('components.autocomplete.technologies.loadingTechnologies')
            : t('components.autocomplete.technologies.technologiesNotFound')
        }
        renderInput={params => (
          <TextField
            id={'combo-box-all-technologies'}
            InputProps={params.InputProps}
            inputProps={{ ...params.inputProps, 'aria-label': t('components.autocomplete.technologies.label') }}
            fullWidth={true}
            size="small"
            label={t('components.autocomplete.technologies.label')}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props
          const isSelected = usersTechnologies.some(item => item.technologyId === option.id)
          return (
            <MenuItem
              key={key}
              {...otherProps}
              disabled={isSelected}
              sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, overflowY: 'hidden' }}
            >
              <TechnologyLogo size={28} technology={option} />

              <StyledContainerColumnLeft>
                <Typography variant="body2" sx={{ color: 'text.highEmphasis' }}>
                  {option.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.base' }}>
                  {formatTechnologyType(option.type)}
                </Typography>
              </StyledContainerColumnLeft>
            </MenuItem>
          )
        }}
        renderGroup={params => (
          <div key={params.key}>
            <Box sx={{ px: 1.5, py: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.highEmphasis', fontWeight: 700, overflowY: 'hidden' }}>
                {formatTechnologyType(params.group)}
              </Typography>
            </Box>
            <Box>{params.children}</Box>
          </div>
        )}
        renderTags={() => null}
      />
    </StyledContainerRowBetween>
  )
}

export default SelectTechnology